import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import { isApiError } from '../client/errors';
import { queryKeys } from '../client/query-client';
import { BillingPeriod } from '../checkout/contracts';
/**
 * `GET /api/subscriptions/mine` — the caller's own subscriptions.
 *
 * A member-facing read scoped to the token. **Empty is a valid answer** — a
 * seat holder on an employer's plan reads `{"items":[]}` here because a
 * business subscription lives against the organisation, not the member (see
 * `session/session.ts`). That is why access is decided by `/entitlement`; this
 * read exists for the manage-my-own screens, which are the only place a
 * subscription id is needed to POST against.
 */
export const PendingPlanChange = z.object({
    planId: z.string().uuid(),
    planKey: z.string(),
    planName: z.string(),
    planVersionId: z.string().uuid(),
    revision: z.number().int(),
    priceCents: z.number().int(),
    currency: z.string(),
    period: BillingPeriod,
    effectiveAt: z.string().datetime().nullable(),
    requestedAt: z.string().datetime(),
});
export const SubscriptionStatus = z.enum([
    'TRIALING',
    'ACTIVE',
    'PAST_DUE',
    'CANCELED',
    'UNPAID',
    'INCOMPLETE',
    'INCOMPLETE_EXPIRED',
    'PAUSED',
]);
/**
 * The price revision a subscription is on — what it is billed, and how often.
 *
 * Declared because the plan screen needs `period`. It used to read the cadence
 * from the key's prefix (`monthly-…`), and the keys migrated from WordPress
 * look like `wp-plateforme-de-formations-la-boussole-du--month1-4900`: they
 * fell through to Annual, and a Monthly member saw Annual marked as theirs.
 * The period says it directly, whatever the key is.
 */
export const SubscriptionPlanVersion = z.object({
    id: z.string().uuid(),
    priceCents: z.number().int(),
    currency: z.string(),
    /*
     * `LIFETIME` as well as the three cadences the catalogue sells. The contract
     * allows it and a member can hold one, and this is required: a narrower enum
     * would fail the parse of the whole `/subscriptions/mine` read for that
     * member — every billing screen, not just the cadence label. The shared
     * `BillingPeriod` stays narrow on purpose; it is what the funnel sells.
     */
    period: z.enum([...BillingPeriod.options, 'LIFETIME']),
});
/** Mirrors `Subscription` on the backend. */
export const Subscription = z.object({
    id: z.string().uuid(),
    userId: z.string().uuid().nullable(),
    organisationId: z.string().uuid().nullable(),
    status: SubscriptionStatus,
    seats: z.number().int(),
    planVersion: SubscriptionPlanVersion,
    planId: z.string().uuid(),
    planKey: z.string(),
    planName: z.string(),
    currentPeriodStart: z.string().datetime().nullable(),
    currentPeriodEnd: z.string().datetime().nullable(),
    trialEndsAt: z.string().datetime().nullable(),
    cancelAtPeriodEnd: z.boolean(),
    canceledAt: z.string().datetime().nullable(),
    pendingPlanChange: PendingPlanChange.nullable(),
    stripeSubscriptionId: z.string().nullable(),
    entitled: z.boolean(),
    createdAt: z.string().datetime(),
});
export const MySubscriptionsResponse = z.object({
    items: z.array(Subscription),
});
const SUBSCRIPTIONS_QUERY_KEY = ['subscriptions', 'mine'];
export function useMySubscriptions(http) {
    return useQuery({
        queryKey: SUBSCRIPTIONS_QUERY_KEY,
        queryFn: () => http.get('/subscriptions/mine', { schema: MySubscriptionsResponse }),
        staleTime: 30_000,
    });
}
/**
 * `POST /api/subscriptions/mine/{id}/change-plan` — schedule a plan move at
 * the next renewal.
 *
 * The contract takes `planId` (uuid), not a key — a client naming a revision
 * could name a retired one, so the server resolves the sellable revision from
 * the plan itself. Nothing is charged and nothing is prorated: the current
 * `planVersion` is untouched until `currentPeriodEnd` arrives.
 *
 * Refusals to surface: SUBSCRIPTION_NOT_FOUND, SUBSCRIPTION_ALREADY_CANCELED,
 * SUBSCRIPTION_NOT_LIVE, SUBSCRIPTION_CANCELLING, SUBSCRIPTION_PLAN_UNCHANGED,
 * PLAN_NOT_FOUND, PLAN_NOT_SELLABLE.
 *
 * On success we invalidate `subscriptions.mine` and the shared `entitlement`
 * key so any "current plan" chip elsewhere re-reads what the server now holds.
 */
export const ChangePlan = z.object({ planId: z.string().uuid() });
export function useChangeMySubscriptionPlan(http) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: ({ id, planId }) => http.post(`/subscriptions/mine/${id}/change-plan`, ChangePlan.parse({ planId }), {
            schema: Subscription,
        }),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: SUBSCRIPTIONS_QUERY_KEY });
            void queryClient.invalidateQueries({ queryKey: queryKeys.entitlementAll });
        },
    });
}
/**
 * Cancel a scheduled switch; the member stays on their current plan.
 *
 * The only way back. Choosing the current plan again is refused as
 * `SUBSCRIPTION_PLAN_UNCHANGED`, because it is the plan they are on.
 */
export function useWithdrawPlanChange(http) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => http.post(`/subscriptions/mine/${id}/change-plan/withdraw`, {}, { schema: Subscription }),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: SUBSCRIPTIONS_QUERY_KEY });
            void queryClient.invalidateQueries({ queryKey: queryKeys.entitlementAll });
        },
    });
}
/**
 * Which of a member's subscriptions the billing screens are about.
 *
 * ── Why this is not `items[0]` ────────────────────────────────────────────
 * It was, and that is the bug reported on 1 October: « when I am trying to
 * upgrade my plan everything is disabled ». The contract promises no ordering,
 * and members DO hold more than one row — on the local database alone, one
 * member has two cancelled subscriptions and an e2e account has two active
 * ones. When a dead or WordPress-migrated row sorts first, the screen reads
 * ITS missing `stripeSubscriptionId`, decides the plan cannot be changed here,
 * and disables a switch the member is perfectly entitled to make.
 *
 * The order of preference:
 *   1. a live subscription — the one a member is actually paying for, and the
 *      only one there is anything to change or cancel on,
 *   2. whatever exists, so a fully cancelled account shows its last one
 *      rather than an empty screen.
 *
 * There was a tier above those two: a live subscription Stripe bills, because
 * only those could be switched. The backend retired that distinction on
 * 2 October (reply 20) and the tier went with it.
 *
 * One rule for every billing screen, so the cadence, the price and the pay
 * path all describe the same subscription instead of three different ones.
 */
const LIVE_STATUSES = ['ACTIVE', 'TRIALING', 'PAST_DUE', 'UNPAID'];
export function isLiveSubscription(subscription) {
    return LIVE_STATUSES.includes(subscription.status);
}
export function currentSubscription(subscriptions) {
    if (!subscriptions || subscriptions.length === 0)
        return null;
    return subscriptions.find(isLiveSubscription) ?? subscriptions[0] ?? null;
}
/**
 * Why a plan change or a withdrawal was refused, as a key.
 *
 * Only the refusals where "try again" would be a lie get their own words: a
 * plan or payment setup that is not ready on our side. Everything else keeps
 * the retry line it always had.
 *
 * `SUBSCRIPTION_NOT_BILLED_BY_STRIPE` was here and is gone — the backend
 * retired the code when it made a plan change work for the subscriptions this
 * platform bills itself (reply 20, 2 Oct). It is out of the contract, so a
 * `case` for it would stop compiling at the next regeneration.
 */
export function planChangeRefusalKey(error) {
    if (!isApiError(error))
        return 'billing.switchError';
    switch (error.code) {
        case 'PLAN_HAS_NO_STRIPE_PRICE':
        case 'STRIPE_NOT_CONFIGURED':
            return 'billing.switchUnavailable';
        default:
            return 'billing.switchError';
    }
}
/**
 * `POST /api/subscriptions/mine/{id}/cancel` — stop the renewal, keep the
 * access that was paid for.
 *
 * ── Why `immediately` is not a parameter ──────────────────────────────────
 * The contract takes `{ immediately }` and defaults it to `false`. `true` ends
 * access the moment it is called and raises no refund — there is no refund path
 * in the API at all — so it gives up a period the member has already paid for,
 * and it is terminal: `resume` will not bring it back. The product's cancel
 * flow does not offer it, and « Avant de partir » promises the opposite in so
 * many words. A default a caller may override is one somebody eventually
 * overrides, so the hook does not take the argument: `false` is sent, always.
 *
 * Period-end is the whole behaviour the copy describes. `status` does not move,
 * `cancelAtPeriodEnd` is set, and `entitled` keeps answering `true` until
 * `currentPeriodEnd` passes.
 *
 * `Idempotency-Key` is required here and the client mints one for every POST
 * (`client/http.ts`) — this is a money action, and a network retry must not
 * turn a period-end cancellation into something else.
 */
export const CancelSubscription = z.object({ immediately: z.boolean() });
/*
 * `onSettled`, not `onSuccess`, for these two.
 *
 * A refused cancel is usually a refused cancel because the state moved under
 * the screen: `SUBSCRIPTION_CANCELLING` means it is already set to end, and the
 * card is still drawing « renews on ». The copy for that refusal tells the
 * member the card above shows the date, which is only true if we re-read — so
 * the invalidation runs whichever way the write went. A `STRIPE_REQUEST_FAILED`
 * re-read costs one request and confirms nothing changed, which is what that
 * message promises.
 */
export function useCancelMySubscription(http) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => http.post(`/subscriptions/mine/${id}/cancel`, CancelSubscription.parse({ immediately: false }), {
            schema: Subscription,
        }),
        onSettled: () => {
            void queryClient.invalidateQueries({ queryKey: SUBSCRIPTIONS_QUERY_KEY });
            void queryClient.invalidateQueries({ queryKey: queryKeys.entitlementAll });
        },
    });
}
/**
 * `POST /api/subscriptions/mine/{id}/resume` — the way back.
 *
 * Shipped in the same pass as the cancel, and not as a nicety: without it,
 * cancelling is a one-way door and a member who pressed it by mistake has to
 * reach support. It clears `cancelAtPeriodEnd` and nothing else — `status` does
 * not move and `currentPeriodEnd` is unchanged.
 *
 * Already renewing is a **no-op**, answering with the subscription rather than
 * refusing: a switch set to the position it is already in is not an error. A
 * subscription cancelled outright is terminal and answers
 * `SUBSCRIPTION_ALREADY_CANCELED` — subscribing again is the way back, not this.
 *
 * A plan change the cancellation cleared is not restored by resuming; the
 * instruction went with the cancellation and re-scheduling it is one request.
 */
export function useResumeMySubscription(http) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (id) => http.post(`/subscriptions/mine/${id}/resume`, {}, { schema: Subscription }),
        onSettled: () => {
            void queryClient.invalidateQueries({ queryKey: SUBSCRIPTIONS_QUERY_KEY });
            void queryClient.invalidateQueries({ queryKey: queryKeys.entitlementAll });
        },
    });
}
/**
 * Why a cancel or a resume was refused, as a key.
 *
 * The backend made these four deliberately distinct so the screen can say what
 * to do next instead of « not eligible », which is the only reason to map them
 * one by one rather than show one sentence.
 *
 * `SUBSCRIPTION_NOT_FOUND` deliberately has no sentence of its own. It covers a
 * row that is not this member's as well as one that does not exist, and telling
 * the two apart is how a client learns which ids are real.
 *
 * `STRIPE_REQUEST_FAILED` (502) also falls to the default, and that is the
 * honest answer rather than a shrug: the row is written only after Stripe
 * answers, so a failure means nothing changed on either side and trying again
 * is exactly the right advice.
 */
export function cancelRefusalKey(error) {
    if (!isApiError(error))
        return 'billing.cancelError';
    switch (error.code) {
        case 'SUBSCRIPTION_CANCELLING':
            return 'billing.cancelAlreadyEnding';
        case 'SUBSCRIPTION_ALREADY_CANCELED':
            return 'billing.cancelAlreadyEnded';
        case 'SUBSCRIPTION_NOT_LIVE':
            return 'billing.cancelNotLive';
        default:
            return 'billing.cancelError';
    }
}
//# sourceMappingURL=subscriptions.js.map